import { useMobileKeyboardHeight } from '@lemonade-hq/uniclient-theme';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { DISABLE_SNAP_ATTR, ScrollSnapItem } from './useWizardScrollSnapController.css';

interface UseWizardScrollSnapAnimations {
  readonly ref: React.RefObject<HTMLDivElement>;
  readonly disableScrollSnap: (source: string[] | string) => void;
  readonly enableScrollSnap: (source: string[] | string) => void;
  readonly scrollToContent: () => void;
  readonly scrollToHistory: () => void;
}

/**
 * Negative scroll value to scroll to the bottom of the container
 * Safari scrollIntoView is buggy so we have to use this hack
 */
export const NEGATIVE_SCROLL = -99_999;

interface UseWizardScrollSnapControllerProps {
  readonly enabled: boolean;
  readonly contentRef: RefObject<HTMLDivElement>;
}

export const useWizardScrollSnapController = ({
  enabled,
  contentRef,
}: UseWizardScrollSnapControllerProps): UseWizardScrollSnapAnimations => {
  const ref = useRef<HTMLDivElement>(null);
  const timeoutsRefs = useRef<ReturnType<typeof setTimeout>[]>([]);
  const { keyboardHeightRef, resetKeyboardHeight } = useMobileKeyboardHeight({ enabled });

  const scrollToContent = useCallback(
    (behavior: 'instant' | 'smooth' = 'smooth') => {
      let top = 0;

      if (contentRef.current != null) {
        const { minHeight } = getComputedStyle(contentRef.current);

        top = parseInt(minHeight) - contentRef.current.clientHeight;
      }

      ref.current?.scrollTo({ top, behavior });
    },
    [contentRef],
  );

  const scrollToHistory = useCallback(() => {
    ref.current?.scrollTo({ top: NEGATIVE_SCROLL, behavior: 'smooth' });
  }, []);

  const disableScrollSnap = useCallback(
    (source: string[] | string) => {
      /* istanbul ignore if -- @preserve - can't mock null ref */
      if (ref.current == null) return;

      const previousSources = ref.current.getAttribute(DISABLE_SNAP_ATTR)?.split(' ') ?? [];
      const sources = Array.isArray(source) ? source : [source];

      if (previousSources.length === 0 && contentRef.current != null) {
        const { minHeight } = getComputedStyle(contentRef.current);
        /**
         * This ensures the scroll won't jump when the content is longer than the viewport
         * TODO: when the content is scrolled and scroll snap is disabled, this causes the content to jump to the content top
         */
        contentRef.current.style.height = `${parseFloat(minHeight) + keyboardHeightRef.current}px`;
      }

      sources.push(...previousSources.filter(s => !sources.includes(s)));

      const newSource = sources.join(' ').trim();

      ref.current.setAttribute(DISABLE_SNAP_ATTR, newSource);
    },
    [contentRef, keyboardHeightRef],
  );

  const enableScrollSnap = useCallback(
    (source: string[] | string) => {
      /* istanbul ignore if -- @preserve - can't mock null ref */
      if (ref.current == null) return;

      const previousSource = ref.current.getAttribute(DISABLE_SNAP_ATTR) ?? '';
      const sources = Array.isArray(source) ? source : [source];

      if (sources.some(prevSource => previousSource.includes(prevSource))) {
        const newSource = sources.reduce((prev, curr) => prev.replace(curr, '').trim(), previousSource);

        ref.current.setAttribute(DISABLE_SNAP_ATTR, newSource);

        if (newSource === '') {
          ref.current.removeAttribute(DISABLE_SNAP_ATTR);

          // This timeout is a hack to make sure the scroll animation is triggered in Safari
          const timeout = setTimeout(() => {
            const element = contentRef.current;
            if (element == null) return;

            const previousHeight = element.style.height;

            element.style.height = '';

            if (previousSource.includes(ScrollSnapItem.History)) {
              scrollToContent();
            } else {
              ref.current?.scrollTo({
                top: parseInt(previousHeight) - element.clientHeight + keyboardHeightRef.current,
                behavior: 'instant',
              });

              resetKeyboardHeight();
            }
          }, 0);

          timeoutsRefs.current.push(timeout);
        }
      }
    },
    [contentRef, keyboardHeightRef, resetKeyboardHeight, scrollToContent],
  );

  useEffect(() => {
    if (contentRef.current == null || !enabled) return;

    scrollToContent('instant');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, contentRef.current, scrollToContent]);

  return {
    ref,
    disableScrollSnap,
    enableScrollSnap,
    scrollToContent,
    scrollToHistory,
  };
};
